import { z } from 'zod';
import { OpaqueRef } from './ids.js';
import { CurrencyCode, PositiveCents, TimeOfDay, Timezone, Weekday } from './primitives.js';
/**
 * What a business schedules against: a room, a chair, a therapist. LoveSpa has
 * no such concept — one person, one line on the calendar — but the agreement
 * requires `resource_id` on every slot and exclusivity per resource, so it is
 * first-class here. A single-operator tenant gets one auto-created resource and
 * never sees the idea.
 */
export const ResourceKind = z.enum(['room', 'person', 'equipment', 'table_pool']);
export const Resource = z.object({
    id: OpaqueRef,
    name: z.string().min(1),
    kind: ResourceKind,
    /** Seats per unit — a 4-top seats four. */
    capacity: z.number().int().min(1).default(1),
    /** Interchangeable copies (spec §4.1) — eight identical 4-tops are one resource, units 8. */
    units: z.number().int().min(1).default(1),
    active: z.boolean().default(true),
});
/** A sellable treatment. Mirrors the agreement's `/services` entry exactly. */
export const Service = z.object({
    id: OpaqueRef,
    name: z.string().min(1),
    description: z.string().default(''),
    duration_min: z.number().int().min(5).max(24 * 60),
    price_cents: PositiveCents,
    max_party: z.number().int().min(1).default(1),
    active: z.boolean().default(true),
    resources: z.array(OpaqueRef),
    details: z.array(z.string()).default([]),
});
/**
 * Internal fields that never cross the boundary. `bookable_by_partner` is the
 * switch that keeps a treatment in the staff catalog and out of the partner's:
 * deleting a service the owner still performs is not an acceptable way to stop
 * an agent selling it.
 */
export const ServiceInternal = Service.extend({
    buffer_before_min: z.number().int().min(0).max(240).default(0),
    buffer_after_min: z.number().int().min(0).max(240).default(0),
    bookable_by_partner: z.boolean().default(true),
});
/** The catalog as the partner sees it, with the version they cache on. */
export const Catalog = z.object({
    version: z.number().int().min(0),
    currency: CurrencyCode,
    timezone: Timezone,
    services: z.array(Service),
});
/** One open stretch of a day, `[start, end)` in the tenant's timezone. */
export const TimeWindow = z.object({
    start: TimeOfDay,
    end: TimeOfDay,
});
/**
 * One day of opening hours — a **list** of windows, because a restaurant opens
 * 11:30–14:00 and again 17:30–22:00 (spec 4.6). A spa's day is one window, and
 * the LoveSpa importer maps its single range to exactly that. `closed` wins
 * over whatever windows are present.
 */
export const DayHours = z.object({
    weekday: Weekday,
    closed: z.boolean().default(false),
    windows: z.array(TimeWindow).default([]),
});
/** Monday-first, seven entries — the same shape LoveSpa stores in `meta`. */
export const WeekHours = z.array(DayHours).length(7);
//# sourceMappingURL=catalog.js.map